import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Loading from "components/Loader/Loading";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { UserCheck } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { adminGetAllSubscriptions } from "redux/features/subscription/subscriptionSlice";
import Input from "shared/Input/Input";
import { CancelSubscriptionModal } from "./CancelSubscriptionModal";
import DynamicTable from "./DynamicTable";
import SubscriptionDetailPage from "./SubscriptionDetailPage";
import { RefundSubscriptionModal } from "./RefundSubscriptionModal";

export interface UserSubscriptionPageProps {
  className?: string;
}

const UserSubscriptionPage: FC<UserSubscriptionPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const { allSubscriptions, loading } = useAppSelector((state) => state.subscription);
  const [searchTerm, setSearchTerm] = useState("");
  const [startingAfter, setStartingAfter] = useState<string | undefined>();
  const [endingBefore, setEndingBefore] = useState<string | undefined>();
  const [modalState, setModalState] = useState<{
    isViewModalOpen: boolean;
    isCancelModalOpen: boolean;
    isRefundModalOpen: boolean;
    selectedItem: any | null;
  }>({
    isViewModalOpen: false,
    isCancelModalOpen: false,
    isRefundModalOpen: false,
    selectedItem: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    dispatch(
      adminGetAllSubscriptions({
        limit: 10,
        email: debouncedSearchTerm || undefined,
        starting_after: startingAfter,
        ending_before: endingBefore,
      })
    );
  }, [dispatch, debouncedSearchTerm, startingAfter, endingBefore]);

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleViewModalOpen = (subscription: any) => {
    setModalState({
      selectedItem: subscription,
      isViewModalOpen: true,
      isCancelModalOpen: false,
      isRefundModalOpen: false,
    });
  };

  const handleCancelModalOpen = (subscription: any) => {
    setModalState({
      selectedItem: subscription,
      isViewModalOpen: false,
      isCancelModalOpen: true,
      isRefundModalOpen: false,
    });
  };

  const handleRefundModalOpen = (subscription: any) => {
    setModalState({
      selectedItem: subscription,
      isViewModalOpen: false,
      isCancelModalOpen: false,
      isRefundModalOpen: true,
    });
  };

  const handleModalClose = () => {
    setModalState({
      selectedItem: null,
      isViewModalOpen: false,
      isCancelModalOpen: false,
      isRefundModalOpen: false,
    });
  };

  const handleNext = () => {
    setEndingBefore(undefined);
    setStartingAfter(allSubscriptions?.nextCursor || undefined);
  };

  const handlePrev = () => {
    setStartingAfter(undefined);
    setEndingBefore(allSubscriptions?.previousCursor || undefined);
  };

  const columns = [
    {
      key: "customer",
      label: "Client",
      render: (item: any) => <span>{item.email}</span>,
    },
    {
      key: "status",
      label: "Statut",
      render: (item: any) => (
        <span
          className={`px-2 py-1 rounded text-white ${
            item.cancel_at ? "bg-red-500" : item.status === "active" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {item.cancel_at
            ? "Annulé"
            : item.status === "trialing"
              ? "En essai"
              : item.status === "active"
                ? "Actif"
                : "Inactif"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (item: any) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewModalOpen(item)}
            className="text-blue-500 transition-colors hover:text-blue-700"
            aria-label={`View ${item.designation || "item"}`}
          >
            <i className="text-xl las la-eye"></i>
          </button>
          <button
            onClick={() => handleCancelModalOpen(item)}
            className="text-green-500 transition-colors hover:text-green-700"
            aria-label={`Update ${item.designation || "item"}`}
          >
            <i className="text-xl las la-edit"></i>
          </button>
          <button
            onClick={() => handleRefundModalOpen(item)}
            className="text-blue-500 transition-colors hover:text-blue-700"
            aria-label={`Update ${item.designation || "item"}`}
          >
            <i className="text-xl las la-sync"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>Abonnements | Tableau de bord administrateur</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <UserCheck className="text-3xl text-blue-500 dark:text-blue-400" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Liste des abonnements</h2>
            </div>
            <div className="relative w-full max-w-lg">
              <Input
                type="text"
                placeholder="Rechercher un client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 shadow-sm pl-11 rounded-xl dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              />
              <MagnifyingGlassIcon
                className={`absolute w-5 h-5 top-1/2 -translate-y-1/2 left-3 ${
                  searchTerm ? "text-blue-500" : "text-gray-400"
                }`}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute text-gray-500 -translate-y-1/2 top-1/2 right-3 hover:text-red-500"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {loading === "pending" ? (
            <Loading />
          ) : (
            <DynamicTable
              columns={columns}
              data={allSubscriptions?.subscriptions || []}
              onNext={allSubscriptions?.hasMore ? handleNext : undefined}
              onPrev={startingAfter || endingBefore ? handlePrev : undefined}
            />
          )}

          {modalState.isViewModalOpen && modalState.selectedItem && (
            <SubscriptionDetailPage
              data={modalState.selectedItem}
              open={modalState.isViewModalOpen}
              handleClose={handleModalClose}
            />
          )}

          {modalState.isRefundModalOpen && modalState.selectedItem && (
            <RefundSubscriptionModal
              subscription={modalState.selectedItem}
              open={modalState.isRefundModalOpen}
              handleClose={handleModalClose}
            />
          )}

          {modalState.isCancelModalOpen && modalState.selectedItem && (
            <CancelSubscriptionModal
              open={modalState.isCancelModalOpen}
              subscription={modalState.selectedItem}
              handleClose={handleModalClose}
            />
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default UserSubscriptionPage;
